import type { DragEndEvent } from '@dnd-kit/core';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Box } from '@mui/material';
import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HeaderRow } from '~/components/list-table';
import {
  sortableCollisionDetection,
  sortableDraggingSx,
  useSortableSensors,
} from '~/hooks/sortable';
import AddPlaylistContentIllustration from '~/images/illustrations/playlist-content.svg';
import { EmptyState } from '../../empty-state';
import { DragGhost } from '../../list-table/DragGhost';
import { SortableRow } from '../../list-table/SortableRow';
import { useSelectAll } from '../../list-table/hooks/useSelectAll';
import type { PlaylistDesign__Playlist as Playlist } from '../index.generated';
import { useColumns } from './columns';
import { EMPTY_ITEMS } from './constants';
import { useOrderedItems } from './hooks/useOrderedItems';

export interface ReorderListProps {
  focusedId?: number | null;
  loading: boolean;
  onCheck: (selectedIds: number[]) => void;
  onDragReorder: (activeId: number, overId: number) => void;
  onRemove?: (id: number) => void;
  onRowFocus: (selectedId: number) => void;
  order: number[];
  playlist?: Playlist;
  readOnly?: boolean;
  search?: string;
  selectedIds: number[];
}

export const ReorderList = memo(function ReorderList({
  focusedId,
  loading,
  onCheck,
  onDragReorder,
  onRemove,
  onRowFocus,
  order,
  playlist,
  readOnly = false,
  search = '',
  selectedIds,
}: ReorderListProps) {
  const { t } = useTranslation();
  const columns = useColumns(readOnly ? undefined : onRemove);
  const sensors = useSortableSensors();
  const items = playlist?.playlistItems ?? EMPTY_ITEMS;
  const isEmpty = items.length === 0;
  const isSearching = search.trim().length > 0;
  const dragDisabled = loading || isSearching || readOnly;

  const [activeId, setActiveId] = useState<number | null>(null);
  const dragging = activeId != null;

  const { orderedItems } = useOrderedItems(order, playlist?.playlistItems);

  const visibleItems = useMemo(
    () =>
      search
        ? orderedItems.filter((i) =>
            i.contentItem.name.toLowerCase().includes(search.toLowerCase()),
          )
        : orderedItems,
    [orderedItems, search],
  );

  const visibleIds = useMemo<number[]>(() => visibleItems.map((i) => i.id), [visibleItems]);

  const { allSelected, someSelected, toggleAll, selectedSet } = useSelectAll(
    visibleIds,
    selectedIds,
    onCheck,
  );

  const handleToggle = useCallback(
    (id: number) => {
      if (selectedSet.has(id)) {
        onCheck(selectedIds.filter((x) => x !== id));
      } else {
        onCheck([...selectedIds, id]);
      }
    },
    [onCheck, selectedIds, selectedSet],
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over) return;

    const activeId = Number(active.id);
    const overId = Number(over.id);
    if (!Number.isFinite(activeId) || !Number.isFinite(overId)) return;

    onDragReorder(activeId, overId);
  };

  const activeItem = items.find((x) => x.id === activeId);

  const rowDisplayOptions = useMemo(
    () => ({
      hideActions: readOnly,
      hideCheckbox: readOnly,
      hideDragHandle: isSearching || readOnly,
    }),
    [readOnly, isSearching],
  );

  return (
    <Box
      data-dragging={dragging ? 'true' : undefined}
      sx={{ position: 'relative', height: '100%', ...sortableDraggingSx }}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={sortableCollisionDetection}
        modifiers={[restrictToVerticalAxis]}
        onDragStart={(e) => {
          if (typeof e.active.id === 'number') setActiveId(e.active.id);
        }}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setActiveId(null)}
      >
        <div role="grid" style={{ display: 'flex', flexDirection: 'column' }}>
          <HeaderRow
            columns={columns}
            allSelected={allSelected}
            someSelected={someSelected}
            onSelectAll={toggleAll}
            options={rowDisplayOptions}
          />
          {!loading && !isEmpty && (
            <SortableContext items={visibleIds} strategy={verticalListSortingStrategy}>
              {visibleItems.map((item) => (
                <SortableRow
                  key={item.id}
                  id={item.id}
                  item={item}
                  columns={columns}
                  checked={selectedSet.has(item.id)}
                  focused={focusedId === item.id}
                  dragDisabled={dragDisabled}
                  onRowFocus={onRowFocus}
                  onToggle={handleToggle}
                  options={rowDisplayOptions}
                />
              ))}
            </SortableContext>
          )}
        </div>

        {/* Outside the grid, which may only hold rows. */}
        {loading ? (
          <Box sx={{ p: 2 }}>Loading…</Box>
        ) : (
          isEmpty && (
            <Box sx={{ display: 'grid', placeItems: 'center', p: 4, height: '100%' }}>
              <EmptyState
                illustration={AddPlaylistContentIllustration}
                header={t('pages.playlists.detail.content.empty_state.header')}
                description={t('pages.playlists.detail.content.empty_state.description')}
              />
            </Box>
          )
        )}

        <DragOverlay dropAnimation={null}>
          {activeItem && <DragGhost name={activeItem.contentItem.name} />}
        </DragOverlay>
      </DndContext>
    </Box>
  );
});
